"use client";

import React from "react";
import { useTranslations } from "next-intl";
import {
  MdOutlineOndemandVideo,
  MdOutlinePictureAsPdf,
  MdLanguage,
  MdErrorOutline,
} from "react-icons/md";
import { ListingTypeEnum } from "@/types/types";
import styles from "./media.module.css";
import PhotosUploader from "./photosUploader";
import SingleMediaUploader from "./singleMediaUploader";
import { useListingContext } from "../listingContex";

export default function MediaSection() {
  const t = useTranslations("NewListing"); // Changed to base namespace for errors
  const { data, updateField, validationErrors } = useListingContext();

  if (!data) return <MediaSkeleton />;

  // Tier Logic
  const canAddVideo = data.listing_type !== ListingTypeEnum.ESSENTIAL;
  const canAddTourAndBrochure =
    data.listing_type === ListingTypeEnum.PRIME ||
    data.listing_type === ListingTypeEnum.SPOTLIGHT;

  const isValidUrl = (urlString: string) => {
    try {
      return Boolean(new URL(urlString));
    } catch (e) {
      return false;
    }
  };

  console.log(data.photos);

  return (
    <section className={styles.transparentContainer}>
      <div className={styles.titleRow}>
        <h2 className={styles.sectionTitle}>Media & Attachments</h2>
      </div>

      {/* 1. PHOTOS (Always Available) */}
      <div id="photos" className={styles.mediaGroup}>
        {validationErrors?.photos && (
          <span className={styles.inlineErrorPhotos}>
            <MdErrorOutline /> {t(`errors.${validationErrors.photos}`)}
          </span>
        )}
        <PhotosUploader />
      </div>

      {/* 2. VIDEO (Plus, Prime, Spotlight) */}
      {canAddVideo && (
        <div
          id="presentation_video"
          className={`${styles.subSection} ${validationErrors?.presentation_video ? styles.errorCard : ""}`}
        >
          <div className={styles.subHeader}>
            <div className={styles.subHeaderLeft}>
              <MdOutlineOndemandVideo className={styles.subIcon} />
              <div>
                <h3 className={styles.subTitle}>Presentation Video</h3>
                <p className={styles.subDescription}>
                  Upload a video walkthrough. Max 100MB.
                </p>
              </div>
            </div>
            {validationErrors?.presentation_video && (
              <span className={styles.inlineError}>
                <MdErrorOutline />{" "}
                {t(`errors.${validationErrors.presentation_video}`)}
              </span>
            )}
          </div>
          <SingleMediaUploader
            category="video"
            storeKey="presentation_video"
            accept={{ "video/mp4": [".mp4"], "video/webm": [".webm"] }}
          />
        </div>
      )}

      {/* 3. VIRTUAL TOUR & BROCHURE (Prime, Spotlight) */}
      {canAddTourAndBrochure && (
        <div className={styles.gridRow}>
          {/* Web Tour */}
          <div
            id="web_tour_url"
            className={`${styles.subSection} ${validationErrors?.web_tour_url ? styles.errorCard : ""}`}
          >
            <div className={styles.subHeader}>
              <div className={styles.subHeaderLeft}>
                <MdLanguage className={styles.subIcon} />
                <div>
                  <h3 className={styles.subTitle}>3D Virtual Tour</h3>
                  <p className={styles.subDescription}>
                    Matterport or Kuula link.
                  </p>
                </div>
              </div>
              {validationErrors?.web_tour_url && (
                <span className={styles.inlineError}>
                  <MdErrorOutline />{" "}
                  {t(`errors.${validationErrors.web_tour_url}`)}
                </span>
              )}
            </div>
            <input
              type="url"
              className={`${styles.textInput} ${validationErrors?.web_tour_url ? styles.inputError : ""}`}
              placeholder="https://my.matterport.com/show/?m=..."
              value={data.web_tour_url || ""}
              onChange={(e) => updateField("web_tour_url", e.target.value)}
            />
            {data.web_tour_url && isValidUrl(data.web_tour_url) && (
              <div className={styles.iframeWrapper}>
                <iframe src={data.web_tour_url} allowFullScreen />
              </div>
            )}
          </div>

          {/* Brochure */}
          <div
            id="brochure"
            className={`${styles.subSection} ${validationErrors?.brochure ? styles.errorCard : ""}`}
          >
            <div className={styles.subHeader}>
              <div className={styles.subHeaderLeft}>
                <MdOutlinePictureAsPdf className={styles.subIcon} />
                <div>
                  <h3 className={styles.subTitle}>Property Brochure</h3>
                  <p className={styles.subDescription}>
                    Upload a PDF presentation.
                  </p>
                </div>
              </div>
              {validationErrors?.brochure && (
                <span className={styles.inlineError}>
                  <MdErrorOutline /> {t(`errors.${validationErrors.brochure}`)}
                </span>
              )}
            </div>
            <SingleMediaUploader
              category="document"
              storeKey="brochure"
              accept={{ "application/pdf": [".pdf"] }}
            />
          </div>
        </div>
      )}
    </section>
  );
}

export function MediaSkeleton() {
  return (
    <section className={styles.transparentContainer}>
      <div className={`${styles.skeletonPulse} ${styles.skelSectionTitle}`} />

      {/* 1. PHOTOS */}
      <div className={styles.photosWrapper}>
        <div className={styles.subHeader}>
          <div>
            <div className={`${styles.skeletonPulse} ${styles.skelSubTitle}`} />
            <div className={`${styles.skeletonPulse} ${styles.skelSubDesc}`} />
          </div>
        </div>

        <div className={styles.photoGrid}>
          {/* Main Dropzone Skeleton */}
          <div className={`${styles.skeletonPulse} ${styles.dropzoneBox}`} />
          {/* Skeleton Images */}
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className={`${styles.skeletonPulse} ${styles.photoCard}`}
            />
          ))}
        </div>
      </div>

      {/* 2. VIDEO SKELETON */}
      <div className={styles.subSection}>
        <div className={styles.subHeader}>
          <div className={`${styles.skeletonPulse} ${styles.skelIcon}`} />
          <div>
            <div className={`${styles.skeletonPulse} ${styles.skelSubTitle}`} />
            <div className={`${styles.skeletonPulse} ${styles.skelSubDesc}`} />
          </div>
        </div>
        <div className={`${styles.skeletonPulse} ${styles.singleDropzone}`} />
      </div>

      {/* 3. VIRTUAL TOUR & BROCHURE SKELETON */}
      <div className={styles.gridRow}>
        <div className={styles.subSection}>
          <div className={styles.subHeader}>
            <div className={`${styles.skeletonPulse} ${styles.skelIcon}`} />
            <div>
              <div
                className={`${styles.skeletonPulse} ${styles.skelSubTitle}`}
              />
              <div
                className={`${styles.skeletonPulse} ${styles.skelSubDesc}`}
              />
            </div>
          </div>
          <div
            className={`${styles.skeletonPulse} ${styles.textInput}`}
            style={{ borderColor: "transparent" }}
          />
        </div>

        <div className={styles.subSection}>
          <div className={styles.subHeader}>
            <div className={`${styles.skeletonPulse} ${styles.skelIcon}`} />
            <div>
              <div
                className={`${styles.skeletonPulse} ${styles.skelSubTitle}`}
              />
              <div
                className={`${styles.skeletonPulse} ${styles.skelSubDesc}`}
              />
            </div>
          </div>
          <div className={`${styles.skeletonPulse} ${styles.singleDropzone}`} />
        </div>
      </div>
    </section>
  );
}
